import React, { useState, useRef, useEffect } from "react";
import {
	Box,
	Typography,
	Breadcrumbs,
	Link,
	Paper,
	Button,
	Divider,
	CircularProgress,
	useTheme,
	useMediaQuery,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	IconButton,
	Tabs,
	Tab,
	Chip,
	Radio,
	FormControlLabel
} from "@mui/material";
import { RichTextReadOnly } from "mui-tiptap";
import { getExtensions } from "../../components/UI/HtmlEditor";
import Grid from "@mui/material/Grid2";
import {
	Edit as EditIcon,
	Lock as LockIcon,
	Visibility as VisibilityIcon,
	Close as CloseIcon,
	Info as InfoIcon,
	PersonSearch as PersonSearchIcon,
	Assignment as AssignmentIcon,
	ThumbUp as ThumbUpIcon,
	ThumbDown as ThumbDownIcon,
	Assessment as AssessmentIcon,
	ContentCopy as ContentCopyIcon,
	Archive as ArchiveIcon,
	RateReview as RateReviewIcon
} from "@mui/icons-material";
import { zoullooColors } from "../../theme";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAuthProvider } from "../../auth/hooks/auth-provider";
import { useProject, useUpdateProjectStatus, useProjectArchiveInfo } from "../../hooks/useProject";
import { calculateWorkingDays } from "../../utils/formatting";
import { useEnumTranslation } from "../../utils/translateEnum";
import {
	ProjectLocation,
	ProjectStatus,
	DurationType,
	ProjectType
} from "../../models/Project";
import { UserType } from "../../models/UserType";
import { UserRole } from "../../models/UserRole";
import ProjectStatusBadge from "../../components/common/ProjectStatusBadge";
import ProjectBriefView from "../../components/project/ProjectBriefView";
import { ProjectContacts } from "../../components/project/ProjectContacts";
import { isProjectInternalUser } from "../../models/ProjectUser";
import { SignatoryClientSection } from "../../components/project/SignatoryClientSection";
import { TalentSelection } from "../../components/project/TalentSelection";
import ProjectFreelancersTable from "../../components/project/ProjectFreelancersTable";
import ProjectDocuments from "../../components/project/ProjectDocuments";
import { useClientViewFreelancers } from "../../hooks/useProjectFreelancerProfiles";
import { format } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import ProposalForm, { ProposalFormHandle } from "./components/ProposalForm";
import {
	useFreelancerProposalByProject,
	useCreateFreelancerProposal
} from "../../hooks/useFreelancerProposal";
import {
	FreelancerProposalStatus,
	FreelancerProposalPricingType
} from "../../models/FreelancerProposal";
import ProposalWorkflowActions from "../../components/proposal/ProposalWorkflowActions";
import IncompleteCompanyInfoDialog from "../../components/proposal/IncompleteCompanyInfoDialog";
import { isFreelancerCompanyInfoComplete } from "../../utils/freelancerCompanyCompletion";
import ProposalConversationThread from "../../components/ProposalConversationThread";
import { useProposalConversations } from "../../hooks/useProposalConversations";
import { useExecuteWorkflowAction } from "../../hooks/useProposalWorkflow";
import { ProposalWorkflowAction } from "../../models/ProposalWorkflow";
import { FreelancerDeclineReason } from "../../models/enums/FreelancerDeclineReason";
import DeclineReasonSelector from "../../components/DeclineReasonSelector";
import { useCurrentFreelancer } from "../../hooks/useFreelancer";
import { useFreelancerProfile } from "../../hooks/useFreelancerProfile";
import FreelancerActivityReports from "../freelancer/FreelancerActivityReports";
import ActivityReportsSummaryTable from "../../components/project/ActivityReportsSummaryTable";
import { useSearchFreelancerActivityReports, useActivityReportsSummary } from "../../hooks/useFreelancerActivityReports";
import { useSnackbarContext } from "../../contexts/useSnackBarContext";
import EvaluationTab from "../../components/project/evaluation/EvaluationTab";
import AccountManagementTab from "../../components/project/accountManagement/AccountManagementTab";
import { useProjectEvaluations } from "../../hooks/useProjectEvaluations";
import { ProjectEvaluationStatus, EvaluatorType } from "../../models/ProjectEvaluation";

// Helper to create a Date that serializes to YYYY-MM-DD format only (DateOnly compatible)
const createDateOnly = (dateString: string): Date => {
	const date = new Date(dateString + "T00:00:00.000Z");
	date.toJSON = () => dateString;
	return date;
};

const ProjectDetails: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const projectId = parseInt(id || "0");

	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuthProvider();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));
	const { showSuccess, showError } = useSnackbarContext();

	// State for project brief dialog
	const [briefDialogOpen, setBriefDialogOpen] = useState(false);
	const [activeTab, setActiveTab] = useState(0);
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

	// State for unsaved changes warning dialog
	const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);
	const [pendingTabChange, setPendingTabChange] = useState<number | null>(null);
	const [isSavingDraft, setIsSavingDraft] = useState(false);

	// State for the dialog shown when a freelancer tries to access the proposal
	// form before completing their professional information.
	const [incompleteCompanyInfoDialogOpen, setIncompleteCompanyInfoDialogOpen] =
		useState(false);

	// Ref to access ProposalForm methods
	const proposalFormRef = useRef<ProposalFormHandle>(null);
	const {
		translateProjectDomain,
		translateMissionType,
		translateProjectType,
		translateProjectLocation,
		translateDurationType
	} = useEnumTranslation();

	// Hooks
	const { data: project, isLoading, error } = useProject(projectId);
	const isClientView = user.type === UserType.Client;
	// Only fetch freelancers for CSM/Admin/Client, not for Freelancers
	const shouldFetchFreelancers = user.type !== UserType.Freelancer;
	const { data: freelancers = [], isLoading: isLoadingFreelancers } =
		useClientViewFreelancers(projectId, isClientView, shouldFetchFreelancers);

	// Check permissions early for later use
	const hasRole = (role: string) => user.roles?.includes(role);

	// Only fetch freelancer proposal if user is a freelancer
	// Polling is disabled by default to prevent form refresh issues
	const { data: existingProposal, refetch: refetchProposal } =
		useFreelancerProposalByProject(user.type === UserType.Freelancer ? projectId : 0);

	// Hooks for project rejection
	const createProposal = useCreateFreelancerProposal();
	const executeWorkflowAction = useExecuteWorkflowAction();
	// Only fetch current freelancer if user is a freelancer
	const { data: currentFreelancer } = useCurrentFreelancer(
		user.type === UserType.Freelancer
	);

	// `useCurrentFreelancer` only returns a stripped-down profile (id +
	// onboarding only — see FreelancerMapper.ToFreelancerProfileResponse on
	// the back). We need the full profile (including `company`) to evaluate
	// whether the freelancer can access the proposal form.
	const { data: currentFreelancerFullProfile } = useFreelancerProfile(
		currentFreelancer?.profile.id ?? 0,
		user.type === UserType.Freelancer && !!currentFreelancer?.profile.id
	);

	// Fetch activity reports using the API hook
	const { data: reports = [] } = useSearchFreelancerActivityReports(
		{
			projectId,
			freelancerProfileId: currentFreelancer?.profile.id
		},
		undefined,
		!!projectId && !!currentFreelancer?.profile.id
	);

	// Fetch activity reports summary for all roles (client, internal, freelancer)
	const { data: activityReportsSummary } = useActivityReportsSummary(
		projectId,
		!!projectId
	);

	// State for project rejection dialog
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

	// State for duplicate project dialog
	const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);

	// State for complete project dialog (Admin only)
	const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

	// State for archive project dialog
	const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

	const [freelancerDeclineReason, setFreelancerDeclineReason] = useState<number | null>(
		null
	);
	const [freelancerDeclineReasonDetails, setFreelancerDeclineReasonDetails] =
		useState("");
	const [isRejecting, setIsRejecting] = useState(false);

	// Hook for updating project status (archive)
	const updateProjectStatusMutation = useUpdateProjectStatus();

	// Load conversations for the freelancer's proposal
	const { data: conversations = [], isLoading: isLoadingConversations } =
		useProposalConversations(existingProposal?.id || 0, !!existingProposal?.id);

	// Load archive info if project is archived
	const { data: archiveInfo } = useProjectArchiveInfo(
		projectId,
		project?.status === ProjectStatus.Archived
	);

	// Load evaluations for completed projects (to detect pending invitation)
	const { data: evaluations = [] } = useProjectEvaluations(
		projectId,
		project?.status === ProjectStatus.Completed
	);

	// State for evaluation invite dialog
	const [evaluationInviteDialogOpen, setEvaluationInviteDialogOpen] = useState(false);
	const [evaluationInviteShown, setEvaluationInviteShown] = useState(false);

	// Show evaluation invite popup when client/freelancer has a pending evaluation
	// Stop showing after 2 displays (persisted in localStorage per evaluation)
	const MAX_INVITE_DISPLAYS = 2;
	useEffect(() => {
		if (evaluationInviteShown || evaluations.length === 0) return;
		if (user.type !== UserType.Client && user.type !== UserType.Freelancer) return;

		const pendingEvaluation = evaluations.find(e => {
			if (e.status !== ProjectEvaluationStatus.Pending) return false;
			if (user.type === UserType.Client && e.evaluatorType === EvaluatorType.Client) return true;
			if (user.type === UserType.Freelancer && e.evaluatorType === EvaluatorType.Freelancer) return true;
			return false;
		});

		if (pendingEvaluation) {
			const storageKey = `evaluation_invite_${pendingEvaluation.id}_count`;
			const displayCount = parseInt(localStorage.getItem(storageKey) || "0", 10);

			if (displayCount >= MAX_INVITE_DISPLAYS) return;

			localStorage.setItem(storageKey, String(displayCount + 1));
			setEvaluationInviteDialogOpen(true);
			setEvaluationInviteShown(true);
		}
	}, [evaluations, user.type, evaluationInviteShown]);

	// Determine if evaluation is urgent (more than 7 days since project completion)
	const isEvaluationUrgent = React.useMemo(() => {
		if (!project?.updatedAt) return false;
		const completedDate = new Date(project.updatedAt);
		const now = new Date();
		const diffMs = now.getTime() - completedDate.getTime();
		const diffDays = diffMs / (1000 * 60 * 60 * 24);
		return diffDays >= 7;
	}, [project?.updatedAt]);

	// Extract talent name and company name from pending evaluation for dialog display
	const pendingEvaluationInfo = React.useMemo(() => {
		const pending = evaluations.find(e => {
			if (e.status !== ProjectEvaluationStatus.Pending) return false;
			if (user.type === UserType.Client && e.evaluatorType === EvaluatorType.Client) return true;
			if (user.type === UserType.Freelancer && e.evaluatorType === EvaluatorType.Freelancer) return true;
			return false;
		});
		return {
			talentFullName: pending ? `${pending.freelancerFirstName ?? ""} ${pending.freelancerLastName ?? ""}`.trim() : "",
			companyName: pending?.evaluatorCompanyName ?? project?.company?.tradingName ?? project?.company?.legalName ?? ""
		};
	}, [evaluations, user.type, project?.company]);

	const isInternalUser =
		user.type === UserType.Internal || hasRole(UserRole.CSM) || hasRole(UserRole.Admin);

	// Clients can only edit draft projects, internal users can edit any non-archived project
	// Nobody can edit archived projects
	const canEditProject =
		project &&
		project.status !== ProjectStatus.Archived &&
		((user.type === UserType.Client && project.status === ProjectStatus.Draft) ||
			hasRole(UserRole.CSM) ||
			hasRole(UserRole.Admin));

	// Contact management is available for CSMs, project creator, and associated clients (not for draft projects)
	const canManageContacts =
		project &&
		project.status !== ProjectStatus.Draft &&
		(hasRole(UserRole.CSM) ||
		 hasRole(UserRole.Admin) ||
		 user.id === project.createdById ||
		 (user.type === UserType.Client && project.associatedUsers?.some(pu => pu.id === user.id)));

	// Project brief is available for projects that have been submitted
	const hasProjectBrief = project && project.status !== ProjectStatus.Draft;

	// Check if any freelancer has an accepted proposal
	const hasAcceptedProposal = freelancers.some(
		(f: { status: string }) => f.status === FreelancerProposalStatus.Accepted
	);

	// Talent selection is only available for CSMs/Admins and not for draft projects
	// Also hidden when a proposal has already been accepted
	const canSelectTalents =
		project &&
		project.status !== ProjectStatus.Draft &&
		!hasAcceptedProposal &&
		(hasRole(UserRole.CSM) || hasRole(UserRole.Admin));

	// Duplication is available for CSMs and Admins
	const canDuplicateProject = hasRole(UserRole.CSM) || hasRole(UserRole.Admin);

	// Archiving is available for CSMs and Admins, except for already archived projects
	const canArchiveProject =
		project &&
		project.status !== ProjectStatus.Archived &&
		(hasRole(UserRole.CSM) || hasRole(UserRole.Admin));

	// Show evaluation tab when project is completed
	// For freelancers: only if their proposal was accepted (they have an evaluation assigned)
	const showEvaluationTab =
		project &&
		project.status === ProjectStatus.Completed &&
		(user.type !== UserType.Freelancer || existingProposal?.status === FreelancerProposalStatus.Accepted);

	// Show Account Management tab when:
	// - User is CSM / TAM / Admin
	// - The project has an accepted freelancer proposal (otherwise no AM tasks exist)
	// - The project is not a Draft
	const showAccountManagementTab =
		(hasRole(UserRole.CSM) || hasRole(UserRole.TAM) || hasRole(UserRole.Admin)) &&
		hasAcceptedProposal &&
		project?.status !== ProjectStatus.Draft;

	// Check if current user can edit AM data:
	// - is the CSM Lead for this project
	// - OR is an Admin (override for support / fix purposes)
	const isCurrentUserCsmLead: boolean =
		(project?.associatedUsers?.some(
			(pu) => pu.id === user.id && isProjectInternalUser(pu) && pu.isLead === true
		) ?? false) ||
		!!hasRole(UserRole.Admin);

	// Calculate dynamic tab indices
	const proposalTabIndex = canSelectTalents ? 2 : 1; // If talent selection tab is shown, proposal is tab 2, otherwise tab 1

	// Evaluation tab index: comes after all other tabs
	const hasActivityReportsTab =
		user.type === UserType.Freelancer &&
		existingProposal?.status === FreelancerProposalStatus.Accepted &&
		reports.length > 0;
	const hasProposalTab = user.type === UserType.Freelancer && project?.status !== ProjectStatus.Draft;
	const evaluationTabIndex =
		1 + // Project tab
		(canSelectTalents ? 1 : 0) +
		(hasProposalTab ? 1 : 0) +
		(hasActivityReportsTab ? 1 : 0);

	// Account Management tab index: comes after evaluation tab
	const accountManagementTabIndex = evaluationTabIndex + (showEvaluationTab ? 1 : 0);

	const handleEdit = () => {
		navigate(`/projects/${projectId}/edit`);
	};

	const handleDuplicateClick = () => {
		setDuplicateDialogOpen(true);
	};

	const handleConfirmDuplicate = () => {
		setDuplicateDialogOpen(false);
		navigate(`/projects/create?duplicateFrom=${projectId}`);
	};

	const handleCancelDuplicate = () => {
		setDuplicateDialogOpen(false);
	};

	// Archive project handlers
	const handleArchiveClick = () => {
		setArchiveDialogOpen(true);
	};

	const handleConfirmArchive = async () => {
		try {
			await updateProjectStatusMutation.mutateAsync({
				id: projectId,
				statusUpdate: { status: ProjectStatus.Archived }
			});
			setArchiveDialogOpen(false);
			showSuccess(t("project.archive.success"));
		} catch (error) {
			console.error("Error archiving project:", error);
			showError(t("project.archive.error"));
		}
	};

	const handleCancelArchive = () => {
		setArchiveDialogOpen(false);
	};

	// Complete project handler (Admin only)
	const handleConfirmComplete = async () => {
		try {
			await updateProjectStatusMutation.mutateAsync({
				id: projectId,
				statusUpdate: { status: ProjectStatus.Completed }
			});
			setCompleteDialogOpen(false);
			showSuccess(t("project.complete.success"));
		} catch (error) {
			console.error("Error completing project:", error);
			showError(t("project.complete.error"));
		}
	};

	// Returns true and opens the warning dialog if a freelancer is trying to
	// open the proposal tab while their professional information is not yet
	// complete (registration in progress or required fields missing).
	const shouldBlockProposalAccessForIncompleteCompany = (newValue: number) => {
		if (newValue !== proposalTabIndex) return false;
		if (user.type !== UserType.Freelancer) return false;
		if (isFreelancerCompanyInfoComplete(currentFreelancerFullProfile)) return false;

		setIncompleteCompanyInfoDialogOpen(true);
		return true;
	};

	const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
		// Block the navigation if the freelancer's onboarding company info is
		// still incomplete and they're trying to reach the proposal tab.
		if (shouldBlockProposalAccessForIncompleteCompany(newValue)) {
			return;
		}

		// Check if we're leaving the proposal tab and there are unsaved changes
		// Only show the dialog for proposals that have never been submitted (NotSubmitted or no proposal)
		// Once submitted, modifications are handled via workflow actions
		const isProposalNeverSubmitted =
			!existingProposal ||
			existingProposal.status === FreelancerProposalStatus.NotSubmitted;

		if (
			activeTab === proposalTabIndex &&
			hasUnsavedChanges &&
			user.type === UserType.Freelancer &&
			isProposalNeverSubmitted
		) {
			setPendingTabChange(newValue);
			setUnsavedChangesDialogOpen(true);
		} else {
			setActiveTab(newValue);
		}
	};

	const handleConfirmTabChange = () => {
		if (pendingTabChange !== null) {
			setActiveTab(pendingTabChange);
			setHasUnsavedChanges(false);
			setPendingTabChange(null);
		}
		setUnsavedChangesDialogOpen(false);
	};

	const handleCancelTabChange = () => {
		setPendingTabChange(null);
		setUnsavedChangesDialogOpen(false);
	};

	const handleSaveDraftAndChangeTab = async () => {
		if (proposalFormRef.current) {
			setIsSavingDraft(true);
			try {
				const saved = await proposalFormRef.current.saveFormChanges();
				if (saved && pendingTabChange !== null) {
					setActiveTab(pendingTabChange);
					setHasUnsavedChanges(false);
					setPendingTabChange(null);
				}
			} catch (error) {
				console.error("Error saving draft:", error);
			} finally {
				setIsSavingDraft(false);
			}
		}
		setUnsavedChangesDialogOpen(false);
	};

	const handleAddFreelancer = () => {
		window.scrollTo({ top: 0, behavior: "smooth" });
		setActiveTab(1);
	};

	const handleNavigateToList = () => {
		if (user.type === UserType.Client || user.type === UserType.Freelancer) {
			navigate("/projects");
		} else {
			navigate("/projects/all");
		}
	};

	const handleViewBrief = () => {
		setBriefDialogOpen(true);
	};

	const handleCloseBrief = () => {
		setBriefDialogOpen(false);
	};

	const handleRejectProject = () => {
		setRejectDialogOpen(true);
	};

	const handleConfirmRejectProject = async () => {
		if (!freelancerDeclineReason || !currentFreelancer?.profile?.id) return;

		setIsRejecting(true);

		try {
			// Step 1: Create a minimal proposal with NotSubmitted status
			const proposalResponse = await createProposal.mutateAsync({
				projectId: projectId,
				freelancerProfileId: currentFreelancer.profile.id,
				status: FreelancerProposalStatus.NotSubmitted,
				relevantExperience: "", // Minimal data for rejection
				projectStrengths: "",
				estimatedStartDate: createDateOnly(new Date().toISOString().split("T")[0]),
				pricingMode: FreelancerProposalPricingType.Daily,
				tasks: [],
				hasExternalCosts: false,
				hasExpenses: false
			});

			// Step 2: Execute RefuseByTalent workflow action on the created proposal
			await executeWorkflowAction.mutateAsync({
				proposalId: proposalResponse.id,
				action: ProposalWorkflowAction.RefuseByTalent,
				freelancerDeclineReason: freelancerDeclineReason,
				freelancerDeclineReasonDetails: freelancerDeclineReasonDetails || undefined
			});

			// Step 3: Refresh proposal data and navigate away
			await refetchProposal();
			setRejectDialogOpen(false);
			handleNavigateToList();
		} catch (error) {
			console.error("Error rejecting project:", error);
		} finally {
			setIsRejecting(false);
		}
	};

	const handleCancelRejectProject = () => {
		setRejectDialogOpen(false);
		setFreelancerDeclineReason(null);
		setFreelancerDeclineReasonDetails("");
	};

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (error || !project) {
		return (
			<Box p={3}>
				<Typography color="error">
					{t("project.errorLoadingProject")}: {error?.message || t("project.notFound")}
				</Typography>
			</Box>
		);
	}

	// Helper function to format daily rate display
	const formatDailyRate = (rate?: number) => {
		if (!rate) return t("project.notCommunicated");
		return `${rate.toLocaleString("fr-FR", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		})} €`;
	};

	// Helper function to calculate mission duration
	const calculateMissionDuration = () => {
		const totalDays = calculateWorkingDays(
			project.duration,
			project.durationType,
			project.fullTime,
			project.daysPerPeriod
		);
		return totalDays > 0 ? totalDays : null;
	};

	return (
		<Box p={isMobile ? 2 : 2}>
			{/* Breadcrumbs */}
			<Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 3 }}>
				<Link
					underline="hover"
					color="inherit"
					onClick={handleNavigateToList}
					sx={{ cursor: "pointer" }}
				>
					{user.type === UserType.Client || user.type === UserType.Freelancer
						? t("project.myProjects")
						: t("project.projectList")}
				</Link>
				<Typography color="text.primary">{project.name}</Typography>
			</Breadcrumbs>

			{/* Edit and Duplicate Buttons */}
			{(canEditProject || canDuplicateProject) && (
				<Box mb={3} display="flex" justifyContent="flex-end" gap={2}>
					{canDuplicateProject && (
						<Button
							variant="outlined"
							startIcon={<ContentCopyIcon />}
							onClick={handleDuplicateClick}
							sx={{
								borderColor: zoullooColors.secondary.main,
								color: zoullooColors.secondary.main,
								"&:hover": {
									borderColor: zoullooColors.secondary.dark,
									backgroundColor: zoullooColors.secondary.alpha
								}
							}}
						>
							{t("project.duplicate.button")}
						</Button>
					)}
					{canEditProject && (
						<Button
							variant="outlined"
							startIcon={<EditIcon />}
							onClick={handleEdit}
							sx={{
								borderColor: zoullooColors.secondary.main,
								color: zoullooColors.secondary.main,
								"&:hover": {
									borderColor: zoullooColors.secondary.dark,
									backgroundColor: zoullooColors.secondary.alpha
								}
							}}
						>
							{t("common.edit")}
						</Button>
					)}
				</Box>
			)}

			{/* Tabs for CSM/Admin users, Freelancers, or when evaluation/AM tab is shown */}
			{(canSelectTalents || user.type === UserType.Freelancer || showEvaluationTab || showAccountManagementTab) && (
				<Box
					sx={{
						borderBottom: 1,
						borderColor: "divider",
						mb: 3,
						borderBottomColor: `${zoullooColors.grey[400]}`
					}}
				>
					<Tabs
						value={activeTab}
						onChange={handleTabChange}
						sx={{
							"& .MuiTab-root": {
								borderRadius: "8px 8px 0 0",
								mr: 1,
								minHeight: 48,
								color: "text.primary"
							},
							"& .MuiTab-root.Mui-selected": {
								backgroundColor: zoullooColors.secondary.light + "20",
								color: `${zoullooColors.secondary.main} !important`,
								"& .MuiSvgIcon-root": {
									color: `${zoullooColors.secondary.main} !important`
								}
							},
							"& .MuiTabs-indicator": {
								backgroundColor: zoullooColors.secondary.main
							}
						}}
					>
						<Tab
							icon={<InfoIcon />}
							iconPosition="start"
							label={t("project.projectTab")}
						/>
						{project.status !== ProjectStatus.Draft && canSelectTalents && (
							<Tab
								icon={<PersonSearchIcon />}
								iconPosition="start"
								label={t("project.talentSelection.title")}
							/>
						)}
						{user.type === UserType.Freelancer &&
							project.status !== ProjectStatus.Draft && (
								<Tab
									icon={<AssignmentIcon />}
									iconPosition="start"
									label={
										existingProposal
											? t("project.myProposal.title")
											: t("project.proposal.title")
									}
								/>
							)}
						{user.type === UserType.Freelancer &&
							existingProposal?.status === FreelancerProposalStatus.Accepted &&
							reports.length > 0 && (
								<Tab
									icon={<AssessmentIcon />}
									iconPosition="start"
									label={t("project.activityReports.title")}
								/>
							)}
						{showEvaluationTab && (
							<Tab
								icon={<RateReviewIcon />}
								iconPosition="start"
								label={t("evaluations.tabTitle")}
							/>
						)}
						{showAccountManagementTab && (
							<Tab
								iconPosition="start"
								label={t("accountManagement.tabTitle", "Account Management")}
							/>
						)}
					</Tabs>
				</Box>
			)}

			{/* Tab Panel 0: Project Details */}
			{activeTab === 0 && (
				<Grid container spacing={2}>
					{/* Main Content Column */}
					<Grid size={{ xs: 12, md: 9 }}>
						{/* Your Need Section */}
						<Paper sx={{ p: 2, mb: 3 }}>
							{/* Project Header inside the paper */}
							<Box mb={3}>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									flexWrap="wrap"
									gap={2}
								>
									<Box>
										<Typography variant="h2" gutterBottom>
											{project.name}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{project.projectNumber}
										</Typography>
									</Box>
									<ProjectStatusBadge status={project.status} size="medium" />
								</Box>
							</Box>

							{/* Message for submitted projects (shown only to clients who created the project themselves) */}
							{project.status === ProjectStatus.Submitted &&
								user.type === UserType.Client &&
								project.createdBy?.type !== UserType.Internal && (
									<Box
										sx={{
											p: 3,
											mb: 3,
											backgroundColor: zoullooColors.info.alpha.light,
											borderRadius: 1
										}}
									>
										<Typography variant="body1" color="text.secondary" align="center">
											{t("project.submittedMessage")}
										</Typography>
									</Box>
								)}

							<Typography variant="h4" color={zoullooColors.secondary.dark} gutterBottom>
								{t("project.yourNeed")}
							</Typography>
							<Divider sx={{ mb: 3 }} />

							{/* Business Objectives */}
							{project.objectives && (
								<Box mb={3}>
									<Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
										{t("project.objectives")}
									</Typography>
									<RichTextReadOnly
										content={project.objectives}
										extensions={getExtensions()}
									/>
								</Box>
							)}

							{/* Mission Details */}
							{project.presentationAndNeeds && (
								<Box mb={3}>
									<Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
										{t("project.presentationAndNeeds")}
									</Typography>
									<RichTextReadOnly
										content={project.presentationAndNeeds}
										extensions={getExtensions()}
									/>
								</Box>
							)}

							{/* Hard Skills */}
							{project.talentHardSkills && (
								<Box mb={3}>
									<Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
										{t("project.hardSkillsRequired")}
									</Typography>
									<RichTextReadOnly
										content={project.talentHardSkills}
										extensions={getExtensions()}
									/>
								</Box>
							)}

							{/* Soft Skills */}
							{project.talentSoftSkills && (
								<Box>
									<Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
										{t("project.softSkillsRequired")}
									</Typography>
									<RichTextReadOnly
										content={project.talentSoftSkills}
										extensions={getExtensions()}
									/>
								</Box>
							)}
						</Paper>
						{/* Internal Notes - Only for CSM/Admin */}
						{isInternalUser && project.internalNotes && (
							<Paper elevation={3} sx={{ p: 2 }}>
								<Box display="flex" alignItems="center" mb={2}>
									<LockIcon sx={{ mr: 1, color: zoullooColors.secondary.dark }} />
									<Typography variant="h4" color={zoullooColors.secondary.dark}>
										{t("project.internalSection")}
									</Typography>
								</Box>
								<Divider sx={{ mb: 2 }} />

								<Typography variant="subtitle2" color="text.secondary" gutterBottom>
									{t("project.internalNotes")}
								</Typography>
								<RichTextReadOnly
									content={project.internalNotes}
									extensions={getExtensions()}
								/>
							</Paper>
						)}

						{/* Project Documents */}
						<Box mt={3}>
							<ProjectDocuments
								projectId={projectId}
								projectStatus={project.status}
								createdById={project.createdById}
							/>
						</Box>

						{/* Project Contacts */}
						<Box mt={3}>
							<ProjectContacts
								project={project}
								canEdit={canManageContacts || false}
								currentUser={user}
							/>
						</Box>

						{/* Signatory Client - For CSM/Admin only - Not for Draft projects */}
						{project.status !== ProjectStatus.Draft &&
							(hasRole(UserRole.CSM) || hasRole(UserRole.Admin)) && (
								<Box mt={3}>
									<SignatoryClientSection
										project={project}
										canEdit={!!(hasRole(UserRole.CSM) || hasRole(UserRole.Admin))}
									/>
								</Box>
							)}

						{/* Activity Reports Summary Table — after Signatory Client */}
						{activityReportsSummary?.freelancerSummary && (
							<Box mt={3}>
								<ActivityReportsSummaryTable
									projectId={projectId}
									userType={user.type}
									isInternalUser={!!isInternalUser}
								/>
							</Box>
						)}

						{/* Associated Freelancers - For CSM/Admin/Client - Not for Draft projects */}
						{project.status !== ProjectStatus.Draft &&
							(hasRole(UserRole.CSM) ||
								hasRole(UserRole.Admin) ||
								user.type === UserType.Client) && (
								<Box mt={3}>
									<ProjectFreelancersTable
										projectId={projectId}
										freelancers={freelancers}
										isLoading={isLoadingFreelancers}
										feePercentage={project?.feePercentage || 0}
										externalCostsFeePercentage={project?.externalCostsFeePercentage || 0}
										expensesFeePercentage={project?.expensesFeePercentage || 0}
										processingFees={project?.processingFees || 0}
										onAddFreelancer={handleAddFreelancer}
										onViewFreelancerDetails={freelancerKey => {
											navigate(`/applications/talent/${freelancerKey}`);
										}}
										isClientView={user.type === UserType.Client}
										hasAcceptedProposal={hasAcceptedProposal}
									/>
								</Box>
							)}

					</Grid>

					{/* Sidebar Column - Sticky */}
					<Grid size={{ xs: 12, md: 3 }}>
						<Box
							sx={{
								position: isMobile ? "relative" : "sticky",
								top: isMobile ? 0 : 20
							}}
						>
							<Paper
								sx={{
									p: 2,
									"& .MuiGrid-item:nth-of-type(2n)": {
										"& .MuiTypography-root": {
											textAlign: "right"
										}
									}
								}}
							>
								{/* Company */}
								<Grid container spacing={1} sx={{ mb: 2 }}>
									<Grid size={5}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.company")}
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography variant="body1" sx={{ fontWeight: 500 }}>
											{project.company?.tradingName ||
												project.company?.legalName ||
												t("project.notCommunicated")}
										</Typography>
									</Grid>
								</Grid>

								{/* Expertise Category - CSM only */}
								{isInternalUser && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.expertiseCategory")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.domain
													? translateProjectDomain(project.domain)
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Mission Type - CSM only */}
								{isInternalUser && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.missionType")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.missionType
													? translateMissionType(project.missionType)
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Dates */}
								{project.endDate ? (
									<>
										{/* Start Date */}
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.startDate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.startDate || project.desiredStartDate
														? format(
																new Date(project.startDate || project.desiredStartDate!),
																"dd/MM/yyyy"
														  )
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
										{/* End Date */}
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.endDate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{format(new Date(project.endDate), "dd/MM/yyyy")}
												</Typography>
											</Grid>
										</Grid>
									</>
								) : (
									/* Start Date or Desired Start Date - A partir du */
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.startingFrom")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.startDate || project.desiredStartDate
													? format(
															new Date(project.startDate || project.desiredStartDate!),
															"dd/MM/yyyy"
													  )
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Fees - CSM only */}
								{isInternalUser && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.fees")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.feePercentage
													? `${project.feePercentage}%`
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Processing Fees - CSM only */}
								{isInternalUser && project.processingFees && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.processingFees")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{new Intl.NumberFormat("fr-FR", {
													style: "currency",
													currency: "EUR",
													minimumFractionDigits: 0,
													maximumFractionDigits: 0
												}).format(project.processingFees)}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Daily Rate - Only for Time & Material projects, hidden for Freelancers */}
								{project.type === ProjectType.TimeAndMaterial &&
									user.type !== UserType.Freelancer && (
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.desiredDailyRate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{formatDailyRate(project.dailyRate)}
												</Typography>
											</Grid>
										</Grid>
									)}

								{/* Budget for Fixed Price projects, hidden for Freelancers */}
								{project.type === ProjectType.FixedPrice &&
									user.type !== UserType.Freelancer && (
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.budget")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.budget
														? `${project.budget.toLocaleString("fr-FR", {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2
														  })} €`
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
									)}

								{/* Mission Modality */}
								<Box mb={2}>
									<Grid container spacing={1}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.missionModality")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.type
													? translateProjectType(project.type)
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
									{/* Only show duration details for Time & Material projects */}
									{project.type === ProjectType.TimeAndMaterial && (
										<>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ mt: 1, ml: 1 }}
											>
												{project.fullTime === false && project.daysPerPeriod && (
													<>
														{project.daysPerPeriod}{" "}
														{project.durationType === DurationType.Months
															? t("project.daysPerMonthFor")
															: t("project.daysPerWeekFor")}{" "}
														{project.duration}{" "}
														{project.durationType
															? translateDurationType(project.durationType)
															: ""}
													</>
												)}
												{project.fullTime === true && project.duration && (
													<>
														{t("project.fullTime")} - {project.duration}{" "}
														{project.durationType
															? translateDurationType(project.durationType)
															: ""}
													</>
												)}
												{project.fullTime === null &&
													project.duration &&
													t("project.notCommunicated")}
											</Typography>
											{project.duration && (
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ mt: 1, ml: 1 }}
												>
													{t("project.missionDuration")}:{" "}
													{calculateMissionDuration() ||
														project.workingDays ||
														project.duration}{" "}
													{t("project.days")}
												</Typography>
											)}
										</>
									)}
								</Box>

								{/* Mission Location*/}
								{
									<Box mb={2}>
										<Grid container spacing={1}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.missionLocation")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{translateProjectLocation(project.location)}
												</Typography>
											</Grid>
										</Grid>
										{project.location === ProjectLocation.Hybrid &&
											project.hybridDetails && (
												<Box sx={{ mt: 1, ml: 1 }}>
													<RichTextReadOnly
														content={project.hybridDetails}
														extensions={getExtensions()}
													/>
												</Box>
											)}
									</Box>
								}

								{/* Team Integration */}
								<Grid container spacing={1} sx={{ mb: 3 }}>
									<Grid size={5}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.teamIntegration")}
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography variant="body1">
											{(() => {
												if (
													project.teamIntegration === undefined ||
													project.teamIntegration === null
												) {
													return t("project.notCommunicated");
												}
												return project.teamIntegration ? t("common.yes") : t("common.no");
											})()}
										</Typography>
									</Grid>
								</Grid>

								{/* View Initial Brief Button */}
								{hasProjectBrief && (
									<Button
										fullWidth
										variant="outlined"
										startIcon={<VisibilityIcon />}
										onClick={handleViewBrief}
										sx={{
											borderColor: zoullooColors.secondary.main,
											color: zoullooColors.secondary.main,
											"&:hover": {
												borderColor: zoullooColors.secondary.dark,
												backgroundColor: zoullooColors.secondary.alpha
											},
											mb:
												user.type === UserType.Freelancer &&
												project.status === ProjectStatus.Recruitment
													? 2
													: 0
										}}
									>
										{t("project.brief.viewInitialBrief")}
									</Button>
								)}

								{/* Freelancer Action Buttons - Only show if no proposal exists yet */}
								{user.type === UserType.Freelancer &&
									project.status !== ProjectStatus.Draft &&
									!existingProposal && (
										<Box sx={{ mt: 2 }}>
											<Button
												fullWidth
												variant="contained"
												startIcon={<ThumbUpIcon />}
												onClick={() => {
													if (
														!shouldBlockProposalAccessForIncompleteCompany(proposalTabIndex)
													) {
														setActiveTab(proposalTabIndex);
													}
												}}
												sx={{
													backgroundColor: zoullooColors.primary.main,
													color: "white",
													mb: 2,
													"&:hover": {
														backgroundColor: zoullooColors.primary.dark
													}
												}}
											>
												{t("project.proposal.makeProposal")}
											</Button>
											<Button
												fullWidth
												variant="outlined"
												startIcon={<ThumbDownIcon />}
												onClick={handleRejectProject}
												sx={{
													borderColor: zoullooColors.error.main,
													color: zoullooColors.error.main,
													"&:hover": {
														borderColor: zoullooColors.error.dark,
														backgroundColor: zoullooColors.error.main + "10"
													}
												}}
											>
												{t("project.proposal.rejectProject")}
											</Button>
										</Box>
									)}

								{/* Show button when proposal already exists */}
								{user.type === UserType.Freelancer && existingProposal && (
									<Box sx={{ mt: 2 }}>
										<Button
											fullWidth
											variant="outlined"
											startIcon={<AssignmentIcon />}
											onClick={() => {
												if (
													!shouldBlockProposalAccessForIncompleteCompany(proposalTabIndex)
												) {
													setActiveTab(proposalTabIndex);
												}
											}}
											sx={{
												borderColor: zoullooColors.secondary.main,
												color: zoullooColors.secondary.main,
												"&:hover": {
													borderColor: zoullooColors.secondary.dark,
													backgroundColor: zoullooColors.secondary.alpha
												}
											}}
										>
											{t("project.proposal.viewProposal")}
										</Button>
									</Box>
								)}
							</Paper>

							{/* Complete Project Button - Admin/CSM, when InProgress */}
							{(hasRole(UserRole.Admin) || hasRole(UserRole.CSM)) &&
								project.status === ProjectStatus.InProgress && (
								<Button
									fullWidth
									variant="outlined"
									onClick={() => setCompleteDialogOpen(true)}
									sx={{
										mt: 2,
										borderColor: zoullooColors.warning.main,
										color: zoullooColors.warning.main,
										"&:hover": {
											borderColor: zoullooColors.warning.dark,
											backgroundColor: "rgba(255, 99, 0, 0.04)"
										}
									}}
								>
									{t("project.complete.button")}
								</Button>
							)}

							{/* Archive Project Button - Outside the Paper, for CSM/Admin only */}
							{canArchiveProject && (
								<Button
									fullWidth
									variant="outlined"
									startIcon={<ArchiveIcon />}
									onClick={handleArchiveClick}
									sx={{
										mt: 2,
										borderColor: zoullooColors.grey[400],
										color: zoullooColors.grey[600],
										"&:hover": {
											borderColor: zoullooColors.grey[600],
											backgroundColor: zoullooColors.grey[100]
										}
									}}
								>
									{t("project.archive.button")}
								</Button>
							)}

							{/* Archive Info Block - Only shown when project is archived */}
							{project.status === ProjectStatus.Archived && archiveInfo && (
								<Paper
									sx={{
										mt: 2,
										p: 2,
										backgroundColor: zoullooColors.grey[50],
										border: `1px solid ${zoullooColors.grey[200]}`
									}}
								>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<ArchiveIcon sx={{ color: zoullooColors.grey[500], fontSize: 20 }} />
										<Typography variant="body2" color="text.secondary">
											{t("project.archive.archivedBy", {
												firstName: archiveInfo.archivedByFirstName,
												lastName: archiveInfo.archivedByLastName,
												date: format(new Date(archiveInfo.archivedAt), "dd/MM/yyyy", {
													locale: i18n.language === "fr" ? fr : enUS
												}),
												time: format(new Date(archiveInfo.archivedAt), "HH:mm", {
													locale: i18n.language === "fr" ? fr : enUS
												})
											})}
										</Typography>
									</Box>
								</Paper>
							)}
						</Box>
					</Grid>
				</Grid>
			)}

			{/* Tab Panel 1: Talent Selection */}
			{activeTab === 1 && canSelectTalents && (
				<Box sx={{ height: "calc(100vh - 250px)" }}>
					<TalentSelection
						projectId={projectId}
						projectTitle={project?.name}
						projectNumber={project?.projectNumber || `PRJ-${projectId}`}
						onTalentsSubmitted={() => {
							// Optionally refresh project data or show success message
						}}
					/>
				</Box>
			)}

			{/* Tab Panel: Freelancer Proposal */}
			{user.type === UserType.Freelancer && project.status !== ProjectStatus.Draft && (
				<Grid
					container
					spacing={2}
					sx={{ display: activeTab === proposalTabIndex ? "flex" : "none" }}
				>
					{/* Main Content Column */}
					<Grid size={{ xs: 12, md: 9 }}>
						<Paper>
							<ProposalForm
								ref={proposalFormRef}
								projectId={projectId.toString()}
								onCancel={() => setActiveTab(0)} // Always go back to Project tab
								onSubmit={() => {
									// Stay on the proposal tab after successful submission
									// The tab will remain visible due to existingProposal being loaded
									setActiveTab(proposalTabIndex);
								}}
								onHasChangesUpdate={setHasUnsavedChanges}
							/>
						</Paper>
					</Grid>

					{/* Sidebar Column - Sticky */}
					<Grid size={{ xs: 12, md: 3 }}>
						<Box
							sx={{
								position: isMobile ? "relative" : "sticky",
								top: isMobile ? 0 : 20
							}}
						>
							<Paper
								elevation={3}
								sx={{
									p: 2,
									"& .MuiGrid-item:nth-of-type(2n)": {
										"& .MuiTypography-root": {
											textAlign: "right"
										}
									}
								}}
							>
								{/* Company */}
								<Grid container spacing={1} sx={{ mb: 2 }}>
									<Grid size={5}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.company")}
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography variant="body1" sx={{ fontWeight: 500 }}>
											{project.company?.tradingName ||
												project.company?.legalName ||
												t("project.notCommunicated")}
										</Typography>
									</Grid>
								</Grid>

								{/* Expertise Category - CSM only */}
								{isInternalUser && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.expertiseCategory")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.domain
													? translateProjectDomain(project.domain)
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Mission Type - CSM only */}
								{isInternalUser && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.missionType")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.missionType
													? translateMissionType(project.missionType)
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Dates */}
								{project.endDate ? (
									<>
										{/* Start Date */}
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.startDate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.startDate || project.desiredStartDate
														? format(
																new Date(project.startDate || project.desiredStartDate!),
																"dd/MM/yyyy"
														  )
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
										{/* End Date */}
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.endDate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{format(new Date(project.endDate), "dd/MM/yyyy")}
												</Typography>
											</Grid>
										</Grid>
									</>
								) : (
									/* Start Date or Desired Start Date - A partir du */
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.startingFrom")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.startDate || project.desiredStartDate
													? format(
															new Date(project.startDate || project.desiredStartDate!),
															"dd/MM/yyyy"
													  )
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Fees - CSM only */}
								{isInternalUser && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.fees")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.feePercentage
													? `${project.feePercentage}%`
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Processing Fees - CSM only */}
								{isInternalUser && project.processingFees && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.processingFees")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{new Intl.NumberFormat("fr-FR", {
													style: "currency",
													currency: "EUR",
													minimumFractionDigits: 0,
													maximumFractionDigits: 0
												}).format(project.processingFees)}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Daily Rate - Only for Time & Material projects */}
								{project.type === ProjectType.TimeAndMaterial &&
									user.type !== UserType.Freelancer && (
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.desiredDailyRate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{formatDailyRate(project.dailyRate)}
												</Typography>
											</Grid>
										</Grid>
									)}

								{/* Budget for Fixed Price projects, hidden for Freelancers */}
								{project.type === ProjectType.FixedPrice &&
									user.type !== UserType.Freelancer && (
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.budget")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.budget
														? `${project.budget.toLocaleString("fr-FR", {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2
														  })} €`
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
									)}

								{/* Mission Modality */}
								<Box mb={2}>
									<Grid container spacing={1}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.missionModality")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.type
													? translateProjectType(project.type)
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
									{/* Only show duration details for Time & Material projects */}
									{project.type === ProjectType.TimeAndMaterial && (
										<>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ mt: 1, ml: 1 }}
											>
												{project.fullTime === false && project.daysPerPeriod && (
													<>
														{project.daysPerPeriod}{" "}
														{project.durationType === DurationType.Months
															? t("project.daysPerMonthFor")
															: t("project.daysPerWeekFor")}{" "}
														{project.duration}{" "}
														{project.durationType
															? translateDurationType(project.durationType)
															: ""}
													</>
												)}
												{project.fullTime === true && project.duration && (
													<>
														{t("project.fullTime")} - {project.duration}{" "}
														{project.durationType
															? translateDurationType(project.durationType)
															: ""}
													</>
												)}
												{project.fullTime === null &&
													project.duration &&
													t("project.notCommunicated")}
											</Typography>
											{project.duration && (
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ mt: 1, ml: 1 }}
												>
													{t("project.missionDuration")}:{" "}
													{calculateMissionDuration() ||
														project.workingDays ||
														project.duration}{" "}
													{t("project.days")}
												</Typography>
											)}
										</>
									)}
								</Box>

								{/* Mission Location*/}
								{
									<Box mb={2}>
										<Grid container spacing={1}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.missionLocation")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{translateProjectLocation(project.location)}
												</Typography>
											</Grid>
										</Grid>
										{project.location === ProjectLocation.Hybrid &&
											project.hybridDetails && (
												<Box sx={{ mt: 1, ml: 1 }}>
													<RichTextReadOnly
														content={project.hybridDetails}
														extensions={getExtensions()}
													/>
												</Box>
											)}
									</Box>
								}

								{/* Team Integration */}
								<Grid container spacing={1} sx={{ mb: 3 }}>
									<Grid size={5}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.teamIntegration")}
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography variant="body1">
											{(() => {
												if (
													project.teamIntegration === undefined ||
													project.teamIntegration === null
												) {
													return t("project.notCommunicated");
												}
												return project.teamIntegration ? t("common.yes") : t("common.no");
											})()}
										</Typography>
									</Grid>
								</Grid>

								{/* Latest Message - Show for Freelancer with existing proposal */}
								{existingProposal &&
									conversations.length > 0 &&
									(() => {
										const latestMessage = conversations[conversations.length - 1];
										const getUserTypeColor = (userType: string): string => {
											switch (userType.toLowerCase()) {
												case "freelancer":
													return zoullooColors.primary.main;
												case "client":
													return zoullooColors.secondary.main;
												case "internal":
													return zoullooColors.success.main;
												default:
													return zoullooColors.grey[450];
											}
										};
										const getUserTypeLabel = (userType: string) => {
											return t(
												`proposal.conversation.userType.${userType.toLowerCase()}`
											);
										};
										const formatDate = (dateString: string) => {
											const date = new Date(dateString);
											const locale = i18n.language === "fr" ? fr : enUS;
											return format(date, "PPp", { locale });
										};

										return (
											<>
												<Divider sx={{ my: 3 }} />
												<Box sx={{ mb: 3 }}>
													<Chip
														label={t("proposal.conversation.latestMessage")}
														size="small"
														sx={{
															backgroundColor: zoullooColors.warning.main,
															color: "white",
															fontWeight: 600,
															fontSize: "0.7rem",
															mb: 1
														}}
													/>
													<Paper
														elevation={4}
														sx={{
															p: 2,
															backgroundColor: "rgba(255, 99, 0, 0.03)",
															borderLeft: 4,
															borderLeftColor: getUserTypeColor(
																latestMessage.senderUserType
															),
															border: 2,
															borderColor: zoullooColors.warning.main
														}}
													>
														<Box
															sx={{
																display: "flex",
																justifyContent: "space-between",
																alignItems: "flex-start",
																mb: 1
															}}
														>
															<Box
																sx={{
																	display: "flex",
																	gap: 1,
																	alignItems: "center",
																	flexWrap: "wrap"
																}}
															>
																<Chip
																	label={getUserTypeLabel(latestMessage.senderUserType)}
																	size="small"
																	variant="outlined"
																	sx={{
																		borderColor: getUserTypeColor(
																			latestMessage.senderUserType
																		),
																		color: zoullooColors.text.primary,
																		fontWeight: 500
																	}}
																/>
																<Typography variant="body2" sx={{ fontWeight: 600 }}>
																	{latestMessage.senderUserName}
																</Typography>
															</Box>
															<Typography variant="caption" color="text.secondary">
																{formatDate(latestMessage.createdAt)}
															</Typography>
														</Box>
														<Typography
															variant="body1"
															sx={{
																mt: 1,
																whiteSpace: "pre-wrap",
																wordBreak: "break-word"
															}}
														>
															{latestMessage.message}
														</Typography>
													</Paper>
												</Box>
											</>
										);
									})()}

								{/* Workflow Actions - Show for Freelancer with existing proposal (not in draft mode) */}
								{existingProposal &&
									existingProposal.status !== FreelancerProposalStatus.NotSubmitted && (
										<>
											<Divider sx={{ my: 3 }} />
											<Box sx={{ mb: 3 }}>
												<ProposalWorkflowActions
													proposalId={existingProposal.id}
													currentStatus={existingProposal.status}
													hasUnsavedChanges={hasUnsavedChanges}
													onBeforeAction={async () => {
														// Save form changes before executing workflow action
														if (hasUnsavedChanges && proposalFormRef.current) {
															const saved =
																await proposalFormRef.current.saveFormChanges();
															if (!saved) {
																console.error("Failed to save form changes");
																return false;
															}
														}
														return true;
													}}
													onActionComplete={() => {
														// Reset unsaved changes after action
														// (Note: useExecuteWorkflowAction already handles all query invalidations)
														setHasUnsavedChanges(false);
													}}
												/>
											</Box>
										</>
									)}

								{/* Full Conversation History - Show for Freelancer with existing proposal */}
								{existingProposal && (
									<>
										<Divider sx={{ my: 3 }} />
										<Box sx={{ mb: 3 }}>
											<ProposalConversationThread
												conversations={conversations}
												isLoading={isLoadingConversations}
												error={null}
											/>
										</Box>
									</>
								)}

								{/* View Initial Brief Button */}
								{hasProjectBrief && (
									<>
										{existingProposal && <Divider sx={{ my: 3 }} />}
										<Button
											fullWidth
											variant="outlined"
											startIcon={<VisibilityIcon />}
											onClick={handleViewBrief}
											sx={{
												borderColor: zoullooColors.secondary.main,
												color: zoullooColors.secondary.main,
												"&:hover": {
													borderColor: zoullooColors.secondary.dark,
													backgroundColor: zoullooColors.secondary.alpha
												},
												mb:
													user.type === UserType.Freelancer &&
													project.status === ProjectStatus.Recruitment
														? 2
														: 0
											}}
										>
											{t("project.brief.viewInitialBrief")}
										</Button>
									</>
								)}
							</Paper>
						</Box>
					</Grid>
				</Grid>
			)}

			{/* Tab Panel: Activity Reports (Freelancer only - for editing reports) */}
			{activeTab === proposalTabIndex + 1 &&
				hasActivityReportsTab && (
					<Grid container spacing={2}>
						{/* Main Content Column */}
						{reports.length > 0 && (
							<Grid size={{ xs: 12, md: 9 }}>
								<FreelancerActivityReports projectId={projectId} />
							</Grid>
						)}

						{/* Sidebar Column - Sticky */}
						<Grid size={{ xs: 12, md: 3 }}>
							<Box
								sx={{
									position: isMobile ? "relative" : "sticky",
									top: isMobile ? 0 : 20
								}}
							>
								<Paper
									elevation={3}
									sx={{
										p: 2,
										"& .MuiGrid-item:nth-of-type(2n)": {
											"& .MuiTypography-root": {
												textAlign: "right"
											}
										}
									}}
								>
									{/* Company */}
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.company")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1" sx={{ fontWeight: 500 }}>
												{project.company?.tradingName ||
													project.company?.legalName ||
													t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>

									{/* Dates */}
									{project.endDate ? (
										<>
											{/* Start Date */}
											<Grid container spacing={1} sx={{ mb: 2 }}>
												<Grid size={5}>
													<Typography variant="subtitle2" color="text.secondary">
														{t("project.startDate")}
													</Typography>
												</Grid>
												<Grid size={7}>
													<Typography variant="body1">
														{project.startDate || project.desiredStartDate
															? format(
																	new Date(
																		project.startDate || project.desiredStartDate!
																	),
																	"dd/MM/yyyy"
															  )
															: t("project.notCommunicated")}
													</Typography>
												</Grid>
											</Grid>
											{/* End Date */}
											<Grid container spacing={1} sx={{ mb: 2 }}>
												<Grid size={5}>
													<Typography variant="subtitle2" color="text.secondary">
														{t("project.endDate")}
													</Typography>
												</Grid>
												<Grid size={7}>
													<Typography variant="body1">
														{format(new Date(project.endDate), "dd/MM/yyyy")}
													</Typography>
												</Grid>
											</Grid>
										</>
									) : (
										/* Desired Start Date */
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.desiredStartDate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.desiredStartDate
														? format(new Date(project.desiredStartDate), "dd/MM/yyyy")
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
									)}

									{/* Fees - CSM only */}
									{isInternalUser && (
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.fees")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.feePercentage
														? `${project.feePercentage}%`
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
									)}

									{/* Daily Rate - Only for Time & Material projects */}
									{project.type === ProjectType.TimeAndMaterial && (
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.desiredDailyRate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{formatDailyRate(project.dailyRate)}
												</Typography>
											</Grid>
										</Grid>
									)}

									{/* Budget for Fixed Price projects */}
									{project.type === ProjectType.FixedPrice && (
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.budget")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.budget
														? `${project.budget.toLocaleString("fr-FR", {
																minimumFractionDigits: 2,
																maximumFractionDigits: 2
														  })} €`
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
									)}

									{/* Mission Modality */}
									<Box mb={2}>
										<Grid container spacing={1}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.missionModality")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.type
														? translateProjectType(project.type)
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
										{/* Only show duration details for Time & Material projects */}
										{project.type === ProjectType.TimeAndMaterial && (
											<>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ mt: 1, ml: 1 }}
												>
													{project.fullTime === false && project.daysPerPeriod && (
														<>
															{project.daysPerPeriod}{" "}
															{project.durationType === DurationType.Months
																? t("project.daysPerMonthFor")
																: t("project.daysPerWeekFor")}{" "}
															{project.duration}{" "}
															{project.durationType
																? translateDurationType(project.durationType)
																: ""}
														</>
													)}
													{project.fullTime === true && project.duration && (
														<>
															{t("project.fullTime")} - {project.duration}{" "}
															{project.durationType
																? translateDurationType(project.durationType)
																: ""}
														</>
													)}
													{project.fullTime === null &&
														project.duration &&
														t("project.notCommunicated")}
												</Typography>
												{project.duration && (
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{ mt: 1, ml: 1 }}
													>
														{t("project.missionDuration")}:{" "}
														{calculateMissionDuration() ||
															project.workingDays ||
															project.duration}{" "}
														{t("project.days")}
													</Typography>
												)}
											</>
										)}
									</Box>

									{/* Mission Location*/}
									{
										<Box mb={2}>
											<Grid container spacing={1}>
												<Grid size={5}>
													<Typography variant="subtitle2" color="text.secondary">
														{t("project.missionLocation")}
													</Typography>
												</Grid>
												<Grid size={7}>
													<Typography variant="body1">
														{translateProjectLocation(project.location)}
													</Typography>
												</Grid>
											</Grid>
											{project.location === ProjectLocation.Hybrid &&
												project.hybridDetails && (
													<Box sx={{ mt: 1, ml: 1 }}>
														<RichTextReadOnly
															content={project.hybridDetails}
															extensions={getExtensions()}
														/>
													</Box>
												)}
										</Box>
									}

									{/* Team Integration */}
									<Grid container spacing={1} sx={{ mb: 3 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.teamIntegration")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{(() => {
													if (
														project.teamIntegration === undefined ||
														project.teamIntegration === null
													) {
														return t("project.notCommunicated");
													}
													return project.teamIntegration
														? t("common.yes")
														: t("common.no");
												})()}
											</Typography>
										</Grid>
									</Grid>
								</Paper>
							</Box>
						</Grid>
					</Grid>
				)}

			{/* Tab Panel: Evaluations */}
			{activeTab === evaluationTabIndex && showEvaluationTab && (
				<Grid container spacing={2}>
					{/* Main Content Column */}
					<Grid size={{ xs: 12, md: 9 }}>
						<EvaluationTab
							projectId={projectId}
							user={user}
							isCSM={!!isInternalUser}
						/>
					</Grid>

					{/* Sidebar Column - Sticky */}
					<Grid size={{ xs: 12, md: 3 }}>
						<Box
							sx={{
								position: isMobile ? "relative" : "sticky",
								top: isMobile ? 0 : 20
							}}
						>
							<Paper
								sx={{
									p: 2,
									"& .MuiGrid-item:nth-of-type(2n)": {
										"& .MuiTypography-root": {
											textAlign: "right"
										}
									}
								}}
							>
								{/* Company */}
								<Grid container spacing={1} sx={{ mb: 2 }}>
									<Grid size={5}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.company")}
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography variant="body1" sx={{ fontWeight: 500 }}>
											{project.company?.tradingName ||
												project.company?.legalName ||
												t("project.notCommunicated")}
										</Typography>
									</Grid>
								</Grid>

								{/* Expertise Category - CSM only */}
								{isInternalUser && (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.expertiseCategory")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.domain
													? translateProjectDomain(project.domain)
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Dates */}
								{project.endDate ? (
									<>
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.startDate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{project.startDate || project.desiredStartDate
														? format(
																new Date(project.startDate || project.desiredStartDate!),
																"dd/MM/yyyy"
														  )
														: t("project.notCommunicated")}
												</Typography>
											</Grid>
										</Grid>
										<Grid container spacing={1} sx={{ mb: 2 }}>
											<Grid size={5}>
												<Typography variant="subtitle2" color="text.secondary">
													{t("project.endDate")}
												</Typography>
											</Grid>
											<Grid size={7}>
												<Typography variant="body1">
													{format(new Date(project.endDate), "dd/MM/yyyy")}
												</Typography>
											</Grid>
										</Grid>
									</>
								) : (
									<Grid container spacing={1} sx={{ mb: 2 }}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.startingFrom")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.startDate || project.desiredStartDate
													? format(
															new Date(project.startDate || project.desiredStartDate!),
															"dd/MM/yyyy"
													  )
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
								)}

								{/* Mission Modality */}
								<Box mb={2}>
									<Grid container spacing={1}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.missionModality")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{project.type
													? translateProjectType(project.type)
													: t("project.notCommunicated")}
											</Typography>
										</Grid>
									</Grid>
									{project.type === ProjectType.TimeAndMaterial && (
										<>
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ mt: 1, ml: 1 }}
											>
												{project.fullTime === false && project.daysPerPeriod && (
													<>
														{project.daysPerPeriod}{" "}
														{project.durationType === DurationType.Months
															? t("project.daysPerMonthFor")
															: t("project.daysPerWeekFor")}{" "}
														{project.duration}{" "}
														{project.durationType
															? translateDurationType(project.durationType)
															: ""}
													</>
												)}
												{project.fullTime === true && project.duration && (
													<>
														{t("project.fullTime")} - {project.duration}{" "}
														{project.durationType
															? translateDurationType(project.durationType)
															: ""}
													</>
												)}
												{project.fullTime === null &&
													project.duration &&
													t("project.notCommunicated")}
											</Typography>
											{project.duration && (
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ mt: 1, ml: 1 }}
												>
													{t("project.missionDuration")}:{" "}
													{calculateMissionDuration() ||
														project.workingDays ||
														project.duration}{" "}
													{t("project.days")}
												</Typography>
											)}
										</>
									)}
								</Box>

								{/* Mission Location */}
								<Box mb={2}>
									<Grid container spacing={1}>
										<Grid size={5}>
											<Typography variant="subtitle2" color="text.secondary">
												{t("project.missionLocation")}
											</Typography>
										</Grid>
										<Grid size={7}>
											<Typography variant="body1">
												{translateProjectLocation(project.location)}
											</Typography>
										</Grid>
									</Grid>
									{project.location === ProjectLocation.Hybrid &&
										project.hybridDetails && (
											<Box sx={{ mt: 1, ml: 1 }}>
												<RichTextReadOnly
													content={project.hybridDetails}
													extensions={getExtensions()}
												/>
											</Box>
										)}
								</Box>

								{/* Team Integration */}
								<Grid container spacing={1} sx={{ mb: 2 }}>
									<Grid size={5}>
										<Typography variant="subtitle2" color="text.secondary">
											{t("project.teamIntegration")}
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography variant="body1">
											{(() => {
												if (
													project.teamIntegration === undefined ||
													project.teamIntegration === null
												) {
													return t("project.notCommunicated");
												}
												return project.teamIntegration ? t("common.yes") : t("common.no");
											})()}
										</Typography>
									</Grid>
								</Grid>
							</Paper>
						</Box>
					</Grid>
				</Grid>
			)}

			{/* Tab Panel: Account Management */}
			{activeTab === accountManagementTabIndex && showAccountManagementTab && (
				<AccountManagementTab
					projectId={projectId}
					isLead={isCurrentUserCsmLead}
					currentUserId={user.id}
					clientUsers={
						project?.associatedUsers
							?.filter((u) => u.type === UserType.Client)
							.map((u) => ({
								id: u.id,
								firstName: u.firstName || "",
								lastName: u.lastName || "",
							})) || []
					}
					freelancerUser={(() => {
						// Find the accepted freelancer of the project (if any)
						// → drives the "talent" recipient chip + dropdown availability
						//   for talent-typed comments (Feedback Talent, CSM Talent Comment, etc.).
						// `freelancers` comes from `useClientViewFreelancers` and is currently
						// untyped at the source — describe the only shape we read from it here.
						const accepted = freelancers.find(
							(f: {
								status: string;
								freelancerUserId?: number;
								freelancerFirstName?: string;
								freelancerLastName?: string;
							}) => f.status === FreelancerProposalStatus.Accepted
						);
						if (!accepted || !accepted.freelancerUserId) return undefined;
						return {
							id: accepted.freelancerUserId,
							firstName: accepted.freelancerFirstName || "",
							lastName: accepted.freelancerLastName || "",
						};
					})()}
				/>
			)}

			{/* Project Brief Dialog */}
			<Dialog
				open={briefDialogOpen}
				onClose={handleCloseBrief}
				maxWidth="lg"
				fullWidth
				fullScreen={isMobile}
			>
				<DialogTitle
					sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
				>
					<Typography variant="h6">{t("project.brief.title")}</Typography>
					<IconButton
						edge="end"
						color="inherit"
						onClick={handleCloseBrief}
						aria-label="close"
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent sx={{ p: 0 }}>
					{briefDialogOpen && <ProjectBriefView projectId={projectId} />}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseBrief} color="secondary">
						{t("common.close")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Project Rejection Dialog */}
			<Dialog
				open={rejectDialogOpen}
				onClose={handleCancelRejectProject}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("project.proposal.rejectProject")}</DialogTitle>
				<DialogContent>
					<DeclineReasonSelector
						type="freelancer"
						reason={freelancerDeclineReason}
						reasonDetails={freelancerDeclineReasonDetails}
						onReasonChange={setFreelancerDeclineReason}
						onReasonDetailsChange={setFreelancerDeclineReasonDetails}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelRejectProject} disabled={isRejecting}>
						{t("common.cancel")}
					</Button>
					<Button
						onClick={handleConfirmRejectProject}
						color="error"
						variant="contained"
						disabled={
							!freelancerDeclineReason ||
							(freelancerDeclineReason === FreelancerDeclineReason.Other &&
								!freelancerDeclineReasonDetails.trim()) ||
							isRejecting
						}
						startIcon={isRejecting ? <CircularProgress size={20} /> : null}
					>
						{isRejecting ? t("common.submitting") : t("common.confirm")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Incomplete company info dialog (proposal access guard).
			    Only mounted when open: the dialog uses an onboarding mutation
			    hook that requires snackbar/progress providers which freelancer
			    routes ship with, but project tests don't. Mounting on demand
			    keeps the dialog isolated from contexts it doesn't need at
			    rest. */}
			{incompleteCompanyInfoDialogOpen && (
				<IncompleteCompanyInfoDialog
					open={incompleteCompanyInfoDialogOpen}
					onClose={() => setIncompleteCompanyInfoDialogOpen(false)}
					freelancerProfile={currentFreelancerFullProfile}
				/>
			)}

			{/* Unsaved Changes Warning Dialog */}
			<Dialog
				open={unsavedChangesDialogOpen}
				onClose={handleCancelTabChange}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("unsavedChangesDialog.title")}</DialogTitle>
				<DialogContent>
					<Typography variant="body1">{t("unsavedChangesDialog.message")}</Typography>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={handleCancelTabChange}
						color="primary"
						disabled={isSavingDraft}
					>
						{t("unsavedChangesDialog.stay")}
					</Button>
					<Button
						onClick={handleSaveDraftAndChangeTab}
						color="secondary"
						variant="contained"
						disabled={isSavingDraft}
						startIcon={
							isSavingDraft ? <CircularProgress size={16} color="inherit" /> : null
						}
					>
						{isSavingDraft
							? t("unsavedChangesDialog.saving")
							: t("unsavedChangesDialog.saveDraft")}
					</Button>
					<Button
						onClick={handleConfirmTabChange}
						color="error"
						variant="contained"
						disabled={isSavingDraft}
					>
						{t("unsavedChangesDialog.leave")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Duplicate Project Confirmation Dialog */}
			<Dialog
				open={duplicateDialogOpen}
				onClose={handleCancelDuplicate}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("project.duplicate.dialogTitle")}</DialogTitle>
				<DialogContent>
					<Typography variant="body1">
						{t("project.duplicate.dialogMessage", { projectName: project.name })}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
						{t("project.duplicate.dialogDetails")}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelDuplicate}>
						{t("common.cancel")}
					</Button>
					<Button
						onClick={handleConfirmDuplicate}
						color="secondary"
						variant="contained"
						startIcon={<ContentCopyIcon />}
					>
						{t("project.duplicate.confirm")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Evaluation Invite Dialog */}
			<Dialog
				open={evaluationInviteDialogOpen}
				onClose={isEvaluationUrgent ? undefined : () => setEvaluationInviteDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<RateReviewIcon sx={{ color: zoullooColors.secondary.main }} />
						{user.type === UserType.Client
							? (isEvaluationUrgent
								? t("evaluations.invite.urgent.clientTitle", { talentName: pendingEvaluationInfo.talentFullName })
								: t("evaluations.invite.clientTitle", { talentName: pendingEvaluationInfo.talentFullName }))
							: (isEvaluationUrgent
								? t("evaluations.invite.urgent.freelancerTitle")
								: t("evaluations.invite.freelancerTitle"))
						}
					</Box>
				</DialogTitle>
				<DialogContent>
					{user.type === UserType.Client ? (
						isEvaluationUrgent ? (
							<>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{t("evaluations.invite.urgent.clientBody1", { projectName: project.name })}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{t("evaluations.invite.urgent.clientBody2")}
								</Typography>
								<FormControlLabel
									control={<Radio checked sx={{ color: zoullooColors.secondary.main, "&.Mui-checked": { color: zoullooColors.secondary.main } }} />}
									label={t("evaluations.invite.urgent.clientRadio")}
									sx={{ ml: 0 }}
								/>
							</>
						) : (
							<>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{t("evaluations.invite.clientBody1", { projectName: project.name })}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{t("evaluations.invite.clientBody2")}
								</Typography>
								<Typography variant="body1" fontWeight="bold">
									{t("evaluations.invite.clientCta")}
								</Typography>
							</>
						)
					) : (
						isEvaluationUrgent ? (
							<>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{t("evaluations.invite.urgent.freelancerBody1", { projectName: project.name, companyName: pendingEvaluationInfo.companyName })}
								</Typography>
								<Typography variant="body1">
									{t("evaluations.invite.urgent.freelancerBody2")}
								</Typography>
							</>
						) : (
							<>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{t("evaluations.invite.freelancerBody1", { projectName: project.name, companyName: pendingEvaluationInfo.companyName })}
								</Typography>
								<Typography variant="body1" sx={{ mb: 2 }}>
									{t("evaluations.invite.freelancerBody2")}
								</Typography>
								<Typography variant="body1" fontWeight="bold">
									{t("evaluations.invite.freelancerCta")}
								</Typography>
							</>
						)
					)}
				</DialogContent>
				<DialogActions>
					{!isEvaluationUrgent && (
						<Button
							onClick={() => setEvaluationInviteDialogOpen(false)}
							variant="outlined"
							sx={{
								borderColor: zoullooColors.grey[400],
								color: zoullooColors.grey[700],
								"&:hover": { borderColor: zoullooColors.grey[600] }
							}}
						>
							{t("evaluations.invite.later")}
						</Button>
					)}
					<Button
						variant="contained"
						onClick={() => {
							setEvaluationInviteDialogOpen(false);
							setActiveTab(evaluationTabIndex);
						}}
						sx={{
							backgroundColor: zoullooColors.secondary.main,
							"&:hover": { backgroundColor: zoullooColors.secondary.dark }
						}}
					>
						{user.type === UserType.Client
							? t("evaluations.invite.evaluateTalent")
							: t("evaluations.invite.evaluateProject")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Complete Project Confirmation Dialog */}
			<Dialog
				open={completeDialogOpen}
				onClose={() => setCompleteDialogOpen(false)}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("project.complete.dialogTitle")}</DialogTitle>
				<DialogContent>
					<Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>
						{t("project.complete.dialogMessage", { projectName: project.name })}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCompleteDialogOpen(false)}>
						{t("common.cancel")}
					</Button>
					<Button
						onClick={handleConfirmComplete}
						variant="contained"
						disabled={updateProjectStatusMutation.isPending}
						sx={{
							backgroundColor: zoullooColors.warning.main,
							"&:hover": { backgroundColor: zoullooColors.warning.dark }
						}}
					>
						{updateProjectStatusMutation.isPending
							? t("common.loading")
							: t("project.complete.confirm")}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Archive Project Confirmation Dialog */}
			<Dialog
				open={archiveDialogOpen}
				onClose={handleCancelArchive}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>{t("project.archive.dialogTitle")}</DialogTitle>
				<DialogContent>
					<Typography variant="body1">
						{t("project.archive.dialogMessage", { projectName: project.name })}
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelArchive}>
						{t("common.cancel")}
					</Button>
					<Button
						onClick={handleConfirmArchive}
						variant="contained"
						startIcon={<ArchiveIcon />}
						disabled={updateProjectStatusMutation.isPending}
						sx={{
							backgroundColor: zoullooColors.grey[600],
							"&:hover": {
								backgroundColor: zoullooColors.grey[700]
							}
						}}
					>
						{updateProjectStatusMutation.isPending
							? t("common.loading")
							: t("project.archive.confirm")}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ProjectDetails;
